import {useCallback, useMemo, useRef} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {endpoints} from '@/api/endpoints'
import {ALL_EVENT_TYPES, ExecutionEventType} from '@/types/background-jobs'
import type {ExecutionStreamEvent, JobExecution, TaskExecution} from '@/types/background-jobs'
import {useEventSourceConnection} from './use-event-source-connection'

interface UseExecutionStreamOptions {
    executionId: string
    enabled?: boolean
    onJobCompleted?: () => void
    onJobFailed?: (errorMessage: string | null) => void
    onJobCancelled?: () => void
}

interface UseExecutionStreamResult {
    isConnected: boolean
    error: string | null
    disconnect: () => void
}

/**
 * Hook for subscribing to SSE events for a job execution.
 * Automatically updates React Query cache with real-time progress.
 */
export function useExecutionStream({
                                       executionId,
                                       enabled = true,
                                       onJobCompleted,
                                       onJobFailed,
                                       onJobCancelled,
                                   }: UseExecutionStreamOptions): UseExecutionStreamResult {
    const queryClient = useQueryClient()
    const disconnectRef = useRef<() => void>(() => {})

    const url = useMemo(
        () => endpoints.executions.streamForExecution(executionId),
        [executionId]
    )

    const onEvent = useCallback((raw: unknown) => {
        const data = raw as ExecutionStreamEvent
        switch (data.eventType) {
            case ExecutionEventType.CONNECTED:
                break

            case ExecutionEventType.JOB_STARTED:
                updateExecutionStatus(data.status)
                break

            case ExecutionEventType.JOB_PROGRESS:
                updateExecutionProgress(data.progress)
                break

            case ExecutionEventType.JOB_COMPLETED:
                updateExecutionStatus(data.status)
                invalidateQueries()
                onJobCompleted?.()
                disconnectRef.current()
                break

            case ExecutionEventType.JOB_FAILED:
                updateExecutionStatus(data.status, data.errorMessage)
                invalidateQueries()
                onJobFailed?.(data.errorMessage)
                disconnectRef.current()
                break

            case ExecutionEventType.JOB_CANCELLED:
                updateExecutionStatus(data.status)
                invalidateQueries()
                onJobCancelled?.()
                disconnectRef.current()
                break

            case ExecutionEventType.TASK_STARTED:
                if (data.taskId) updateTaskStatus(data.taskId, data.status, data.progress)
                break

            case ExecutionEventType.TASK_PROGRESS:
                if (data.taskId) updateTaskProgress(data.taskId, data.progress)
                break

            case ExecutionEventType.TASK_COMPLETED:
                if (data.taskId) updateTaskStatus(data.taskId, data.status, 100)
                break

            case ExecutionEventType.TASK_FAILED:
                if (data.taskId) updateTaskStatus(data.taskId, data.status, data.progress, data.errorMessage)
                break

            case ExecutionEventType.TASK_SKIPPED:
                if (data.taskId) updateTaskStatus(data.taskId, data.status, null, data.errorMessage)
                break
        }

        function updateExecutionStatus(status: string, errorMessage?: string | null) {
            queryClient.setQueryData<JobExecution>(
                // eslint-disable-next-line i18next/no-literal-string
                ['execution', executionId],
                (old) => old ? {...old, status: status as JobExecution['status'], errorMessage: errorMessage ?? old.errorMessage} : old
            )
        }

        function updateExecutionProgress(progress: number | null) {
            if (progress === null) return
            queryClient.setQueryData<JobExecution>(
                // eslint-disable-next-line i18next/no-literal-string
                ['execution', executionId],
                (old) => old ? {...old, progress} : old
            )
        }

        function updateTaskStatus(taskId: string, status: string, progress?: number | null, errorMessage?: string | null) {
            queryClient.setQueryData<TaskExecution[]>(
                // eslint-disable-next-line i18next/no-literal-string
                ['execution-tasks', executionId],
                (old) => old?.map((task) =>
                    task.id === taskId
                        ? {...task, status: status as TaskExecution['status'], progress: progress ?? task.progress, errorMessage: errorMessage ?? task.errorMessage}
                        : task
                )
            )
        }

        function updateTaskProgress(taskId: string, progress: number | null) {
            if (progress === null) return
            queryClient.setQueryData<TaskExecution[]>(
                // eslint-disable-next-line i18next/no-literal-string
                ['execution-tasks', executionId],
                (old) => old?.map((task) =>
                    task.id === taskId ? {...task, progress} : task
                )
            )
        }

        function invalidateQueries() {
            queryClient.invalidateQueries({queryKey: ['execution', executionId]})
            queryClient.invalidateQueries({queryKey: ['execution-tasks', executionId]})
        }
    }, [executionId, queryClient, onJobCompleted, onJobFailed, onJobCancelled])

    const result = useEventSourceConnection({
        url,
        enabled: enabled && !!executionId,
        eventTypes: ALL_EVENT_TYPES,
        onEvent,
    })

    disconnectRef.current = result.disconnect

    return result
}
